import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AnimatedText from "./AnimatedText";
import { smoothScrollTo } from "@/lib/smoothScroll";
import { useSoundEffects } from "@/hooks/useSoundEffects";

const Hero = () => {
  const [scrolled, setScrolled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { clickSound } = useSoundEffects();

  useEffect(() => {
    // Set loaded after initial animations
    const timer = setTimeout(() => {
      setLoaded(true);
    }, 1200);

    // Handle scroll for parallax effects
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const handleExploreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    clickSound(e);

    // Directly use smoothScrollTo with the ID
    smoothScrollTo("#projects");
  };

  return (
    <section className="min-h-screen w-full flex flex-col justify-center relative pt-20 pb-20 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 flex flex-col justify-center">
            <h1 className="sr-only">
              Gaurav Gadhari - Prodigy Programmer, AI Builder, Startup Founder
            </h1>

            {/* Main headline with animated reveal */}
            <div className="overflow-hidden mb-2 md:mb-4">
              <AnimatedText
                text="GAURAV"
                className="font-display text-7xl sm:text-8xl md:text-9xl tracking-tighter mb-0 leading-none"
                tag="h2"
              />
            </div>
            <div className="overflow-hidden">
              <AnimatedText
                text="GADHARI"
                className="font-display text-7xl sm:text-8xl md:text-9xl tracking-tighter leading-none"
                delay={200}
                tag="h2"
              />
            </div>

            {/* Subtitle with delayed appearance and gradient text effect */}
            <p
              className="text-sm md:text-base mt-6 md:mt-10 mb-6 md:mb-8 max-w-xl opacity-0 animate-fade-in relative overflow-hidden"
              style={{ animationDelay: "800ms" }}
            >
              <span className="block relative">
                A coder since 7th grade. AI builder. Tech visionary. Creating
                innovative solutions while most are still brewing their morning
                coffee.
                <span
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"
                  style={{ backgroundSize: "200% 100%" }}
                ></span>
              </span>
            </p>

            {/* CTA Button with hover animation */}
            <div
              className="opacity-0 animate-fade-in"
              style={{ animationDelay: "1000ms" }}
            >
              <a
                href="#projects"
                onClick={handleExploreClick}
                className="inline-block border border-white px-8 py-3 uppercase text-sm tracking-widest transition-all duration-300 relative group overflow-hidden"
                data-target="projects"
              >
                <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                  Explore Projects
                </span>
                <span className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:flex items-end justify-end">
            {/* Profile image with animated border frame */}
            <div
              className="w-full aspect-square bg-white relative overflow-hidden animate-fade-in opacity-0"
              style={{ animationDelay: "500ms" }}
            >
              <div className="absolute inset-2 bg-black flex items-center justify-center">
                <img
                  src="/gaurav-gadhari-profile.png"
                  alt="Gaurav Gadhari - Prodigy Programmer and AI Builder"
                  title="Gaurav Gadhari"
                  width={500}
                  height={500}
                  loading="eager"
                  className="w-full h-full object-cover object-top"
                  itemProp="image"
                />
                {/* Animated corners */}
                <div
                  className={`absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-white ${
                    loaded ? "animate-corner-tl" : ""
                  }`}
                ></div>
                <div
                  className={`absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-white ${
                    loaded ? "animate-corner-tr" : ""
                  }`}
                ></div>
                <div
                  className={`absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-white ${
                    loaded ? "animate-corner-bl" : ""
                  }`}
                ></div>
                <div
                  className={`absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-white ${
                    loaded ? "animate-corner-br" : ""
                  }`}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator with enhanced animation */}
      <div
        className={`absolute bottom-12 left-1/2 transform -translate-x-1/2 flex flex-col items-center animate-pulse-slow opacity-0 animate-fade-in ${
          scrolled ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        style={{
          animationDelay: "1300ms",
          transition: "opacity 0.5s ease-out",
        }}
      >
        <div className="h-12 w-0.5 bg-white relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 bottom-0 animate-line-down"></div>
        </div>
        <span className="mt-2 text-xs uppercase tracking-widest">Scroll</span>
      </div>

      {/* Vertical text decoration with parallax effect */}
      <div
        className={`absolute top-1/2 right-4 transform -translate-y-1/2 hidden lg:block transition-transform duration-1000 ${
          scrolled ? "translate-y-[-55%]" : "translate-y-[-50%]"
        }`}
      >
        <p className="text-vertical text-xs uppercase tracking-widest opacity-60">
          Developer
        </p>
      </div>

      {/* Animated mouse position follower */}
      <div className="mouse-follower hidden lg:block"></div>
    </section>
  );
};

export default Hero;
